/**
 * flags.ts — Feature-flag registry + evaluator (WebPortal mirror).
 *
 * THIRD mirror of the Forge flag registry. The canonical source is
 * `agent/lib/flags.js` in the Forge repo, mirrored by
 * `src/orchestrator/flags.ts` (Forge) and here. KEEP ALL THREE IN SYNC — same
 * discipline as `plans.ts`. When you add/promote/retire a flag in Forge, update
 * this file too so the portal's Flags page shows the right inventory.
 *
 * GA by default: most features have NO flag (just code, live for everyone). Add
 * a flag only to stage a rollout (kind 'rollout', default 'off', delete once GA)
 * or to gain a permanent kill-switch on a GA feature (kind 'ops', default 'ga').
 *
 * Live state lives in the SAME DynamoDB table the orchestrator uses:
 *   CONFIG SK=FLAGS_GLOBAL  → { stages: { <flag>: 'off'|'beta'|'ga' }, killed: { <flag>: true } }
 *   PROJECT#<id> SK=FLAGS   → { overrides: { <flag>: 'force_on'|'force_off' } }
 *   PROJECT#<id> SK=BILLING → { beta: true }
 */

export type FlagStage = 'off' | 'beta' | 'ga';
export type FlagKind = 'rollout' | 'ops';
export type FlagOverride = 'force_on' | 'force_off';

export interface FlagDef {
  kind: FlagKind;
  description: string;
  defaultStage: FlagStage;
  owner: string;
  created: string;
  /** rollout flags only: YYYY-MM-DD the hygiene report nags for deletion after. */
  retire_by?: string;
}

export const STAGES: FlagStage[] = ['off', 'beta', 'ga'];
export const KINDS: FlagKind[] = ['rollout', 'ops'];
export const OVERRIDES: FlagOverride[] = ['force_on', 'force_off'];
export const DEFAULT_STAGE: FlagStage = 'off';

export function defaultStageForKind(kind: FlagKind): FlagStage {
  return kind === 'ops' ? 'ga' : 'off';
}

export const FLAGS: Record<string, FlagDef> = {
  custom_workflow_steps: {
    kind:         'rollout',
    description:  'Launch enterprise custom agent workflow steps at story pipeline trigger points.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-06-29',
    retire_by:    '2026-09-29',
  },
  epic_quotas: {
    kind:         'rollout',
    description:  'Enforce monthly epic-unit quotas at epic start (soft overage + hard-cap hold). Metering runs regardless; this gates enforcement.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-06',
    retire_by:    '2026-10-06',
  },
  test_dispute_backflow: {
    kind:         'rollout',
    description:  'Impl agents dispute a broken QA test (TEST_DISPUTED) → QA adjudicates → impl resumes, bounded x2. OFF → request_human_input fallback.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-07',
    retire_by:    '2026-10-07',
  },
  contract_dispute_backflow: {
    kind:         'rollout',
    description:  'qa/impl dispute the architect contract (CONTRACT_DISPUTED) → architect adjudicates → cascade re-validation. OFF → request_human_input fallback.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-07',
    retire_by:    '2026-10-07',
  },
  dispute_aware_routing: {
    kind:         'rollout',
    description:  'qa-validate TESTING_FAILED router routes a repeated/broken-test failure to QA adjudication instead of re-launching impl to BLOCKED. OFF → legacy routing.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-07',
    retire_by:    '2026-10-07',
  },
  dependency_reopen: {
    kind:         'rollout',
    description:  'Consumer disputes a wrong TESTED producer artifact (DEPENDENCY_DISPUTED) → producer reopened (bounded x1) → consumer resumes. OFF → request_human_input fallback.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-07',
    retire_by:    '2026-11-07',
  },
  migration_framework_baseline: {
    kind:         'rollout',
    description:  'Migration stories capture a pre-conversion Framework behavior baseline (qa-validate on un-converted code, Windows runner) before migration-backend, so parity is old-vs-new. OFF → new-vs-new legacy path.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-15',
    retire_by:    '2026-12-15',
  },
  migration_corewcf: {
    kind:         'rollout',
    description:  'migration-analyst applies the CoreWCF recipe: WCF servers with only CoreWCF-portable bindings reclassify hard→assisted instead of human-required. OFF → WCF servers stay human-required.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-15',
    retire_by:    '2026-12-15',
  },
  migration_dbaware: {
    kind:         'rollout',
    description:  'migration-analyst feeds sproc/table lineage into the assessment (dbAware): DB-heavy projects reclassify mechanical→assisted. OFF → DB access ignored.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-15',
    retire_by:    '2026-12-15',
  },
  migration_seams: {
    kind:         'rollout',
    description:  'migration-analyst proposes namespace extraction seams (proposeSeams) for monolithic single-project solutions. OFF → per-.csproj slicing only.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-15',
    retire_by:    '2026-12-15',
  },
  migration_blazor: {
    kind:         'rollout',
    description:  'migration-analyst treats WebForms as an assisted page-by-page Blazor conversion (behind the facade); migration-backend applies the Blazor recipe. OFF → WebForms stays human-required.',
    defaultStage: 'off',
    owner:        'victor',
    created:      '2026-07-15',
    retire_by:    '2026-12-15',
  },
  auto_safety_net: {
    kind:         'ops',
    description:  'Regular feature epics auto-run the characterization-test safety net (cover touched untested modules before changing them). Kill to make safety-net opt-in (/safety-net) only.',
    defaultStage: 'ga',
    owner:        'victor',
    created:      '2026-07-15',
  },
  prompt_cache: {
    kind:         'ops',
    description:  'Anthropic prompt caching (FORGE-058). Kill to disable caching fleet-wide in an incident.',
    defaultStage: 'ga',
    owner:        'victor',
    created:      '2026-06-29',
  },
};

// ── Pure helpers ─────────────────────────────────────────────────────────────

export function normalizeStage(raw: unknown): FlagStage {
  if (typeof raw !== 'string') return DEFAULT_STAGE;
  const s = raw.toLowerCase().trim() as FlagStage;
  return STAGES.includes(s) ? s : DEFAULT_STAGE;
}

export function normalizeOverride(raw: unknown): FlagOverride | null {
  if (typeof raw !== 'string') return null;
  const o = raw.toLowerCase().trim() as FlagOverride;
  return OVERRIDES.includes(o) ? o : null;
}

export function isKnownFlag(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(FLAGS, name);
}

export function allFlagNames(): string[] {
  return Object.keys(FLAGS);
}

export function kindOf(name: string): FlagKind {
  return isKnownFlag(name) ? FLAGS[name].kind : 'rollout';
}

export function effectiveStage(name: string, stages: Record<string, string> = {}): FlagStage {
  if (!isKnownFlag(name)) return DEFAULT_STAGE;
  if (stages && Object.prototype.hasOwnProperty.call(stages, name)) {
    return normalizeStage(stages[name]);
  }
  return normalizeStage(FLAGS[name].defaultStage ?? defaultStageForKind(FLAGS[name].kind));
}

export interface EvaluateInput {
  stage: FlagStage;
  isBeta?: boolean;
  override?: string | null;
  kill?: boolean;
}

export function evaluateFlag({ stage, isBeta = false, override = null, kill = false }: EvaluateInput): boolean {
  if (kill === true) return false;
  const ov = normalizeOverride(override);
  if (ov === 'force_off') return false;
  if (ov === 'force_on') return true;
  const st = normalizeStage(stage);
  if (st === 'ga') return true;
  if (st === 'beta' && isBeta === true) return true;
  return false;
}

// ── DynamoDB row shapes (read by the portal backend) ─────────────────────────

export interface FlagsGlobalDynamoItem {
  PK: 'CONFIG';
  SK: 'FLAGS_GLOBAL';
  stages?: Record<string, FlagStage>;
  killed?: Record<string, boolean>;
  updated_at?: string;
  updated_by?: string;
}

// ── API DTOs (GET /flags, PUT /flags/:name/stage, PUT /flags/:name/kill) ──────

/** One flag's full state for the global Flags admin table. */
export interface FlagView {
  name: string;
  kind: FlagKind;
  description: string;
  owner: string;
  created: string;
  retire_by?: string;
  defaultStage: FlagStage;
  /** Effective global stage (FLAGS_GLOBAL override if set, else registry default). */
  stage: FlagStage;
  /** Whether the stage is an explicit override vs the registry default. */
  stageIsOverride: boolean;
  killed: boolean;
  /** True when a rollout flag is at GA (the hygiene "please delete me" signal). */
  staleAtGa: boolean;
  /** True when a rollout flag is past its retire_by date. */
  pastRetireBy: boolean;
}

export interface ListFlagsResponse {
  flags: FlagView[];
  updated_at?: string;
  updated_by?: string;
}

export interface SetFlagStageRequest {
  stage: FlagStage;
}

export interface SetFlagKillRequest {
  killed: boolean;
}

export interface FlagMutationResponse {
  flag: FlagView;
}
